import React, { useEffect } from 'react';
import { AutoConfirmedAlert, withLoading } from '@adjust/dashboard-components';

import { PaymentTheme } from '@lib/braintree/braintree';
import { SidebarId } from '@react/constants/sidebars';
import { CreditCardFormContainer } from '@react/Pages/PaymentSetup/CreditCardForm/CreditCardForm.container';
import { Trace, TraceStatus } from '@redux/types';
import './PaymentMethod.scss';

export interface PaymentMethodStateProps {
  isLoading: boolean;
  trace: Trace;
  isAlertOpen: boolean;
  supportedCardTypes: string[];
}

export interface PaymentMethodDispatchProps {
  init: (param: PaymentTheme) => any;
  clean: () => void;
  onSubmit: () => void;
  closeAlert: () => void;
}

export interface PaymentMethodOwnProps {
  closeSidebar: () => void;
}

type PaymentMethodProps = PaymentMethodOwnProps & PaymentMethodStateProps & PaymentMethodDispatchProps;

const PaymentMethodBase: React.FC<PaymentMethodProps> = ({
  trace,
  isAlertOpen,
  supportedCardTypes,
  init,
  onSubmit,
  clean,
  closeSidebar,
  closeAlert,
}: PaymentMethodProps) => {
  useEffect(() => {
    init(PaymentTheme.Light);

    return () => {
      clean();
    };
  }, [init, clean]);

  // Close Alert when the component is unmounting
  useEffect(
    () => () => {
      if (isAlertOpen) {
        closeAlert();
      }
    },
    [isAlertOpen, closeAlert],
  );

  const { status, message } = trace;

  const onAlertConfirm = () => {
    if (status === TraceStatus.Info) {
      closeSidebar();
    } else {
      closeAlert();
    }
  };

  return (
    <div className="content content-panel flex-one flex-box-column">
      <div className="flex-one flex-box-column">
        <div className="content-title-box">
          <h3 className="content-heading">{t('label.payment-method')}</h3>

          <div className="cards-supported">
            {supportedCardTypes.map(card => (
              <i key={card} className={`icon-${card}`} />
            ))}
          </div>
        </div>

        <div className="panel-content flex-box-column">
          <div className="inner-content flex-one flex-box-column">
            <CreditCardFormContainer showCancelButton onCancel={closeSidebar} onSubmit={onSubmit} />
          </div>
        </div>

        {isAlertOpen && (
          <AutoConfirmedAlert
            isOpen
            type={status}
            message={message}
            onConfirm={onAlertConfirm}
            parentSidebarId={SidebarId.PaymentMethodDetails}
            closeTimeoutMS={0}
          />
        )}
      </div>
    </div>
  );
};

export const PaymentMethod = withLoading(PaymentMethodBase, true);
